import { useLazyQuery } from '@apollo/client';
import { Button, Dropdown, Segmented, Space, Tag } from 'antd';
import { includes } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import { AddButton, Export } from '../../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  DEFAULTDATEFORMAT,
  DEFAULTDATETIMEFORMATFORREPORTS,
  GA_EVENT,
  GA_LABEL,
  REGISTER_STATUS_CLASSNAME,
  REGISTER_STATUS_LABEL,
  REGISTER_VIEW_TYPE,
  ROUTES,
  TAB_KEYS,
} from '../../../../../../../common/constants';
import { Event } from '../../../../../../../common/trackEvents';
import useRouter from '../../../../../../../common/useRouter';
import CanPerform from '../../../../../../../components/CanPerform';
import CustomRangePicker from '../../../../../../dashboard/components/CustomRangePicker';
import { GET_REGISTER } from '../../../../../graphql/Queries';
import EntriesTable from './EntriesTable';
import SetsTable from './SetsTable';

const RegisterDetails = ({ registerId }) => {
  const {
    navigate,
    params: { projectId },
    location: { pathname, state },
  } = useRouter();
  const registerNavFilter = state?.registerNavFilter;
  const { getCurrentUser, getToken, getCurrentTenant, getTenantUser } =
    useContext(AppContext);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const tenantId = getCurrentTenant() || getTenantUser()?.tenant?.id;
  const currentUser = getCurrentUser();
  const [showListView, setShowListView] = useState(true);
  const [registerDetailsData, setRegisterDetailsData] = useState([]);
  const [isLogsDataEmpty, setIsLogsDataEmpty] = useState(true);
  const [isEntriesDataEmpty, setIsEntriesDataEmpty] = useState(true);
  const [filterOption, setFilterOption] = useState('');
  const [getRegisterDetails] = useLazyQuery(GET_REGISTER, {
    fetchPolicy: 'cache-and-network',
    onError() {},
    onCompleted(res) {
      setRegisterDetailsData(res?.getRegister);
    },
  });

  useEffect(() => {
    if (registerId) {
      getRegisterDetails({ variables: { getRegisterId: registerId } });
    }
  }, []);
  // Commented for future use
  // const renderDownloadPdfButton = () => {
  //   const token = getToken();
  //   const url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/report/register/${showListView ? 'logs' : 'entries'}?access_token=Bearer ${token}&tenantId=${tenantId}&registerId=${registerId}`;
  //   return (
  //     <Button
  //       type="ghost"
  //       className="export-button d-flex b-0 justify-center width-percent-100"
  //       target="_blank"
  //       href={!isDataEmpty && url}
  //       onClick={() => {
  //         Event(GA_EVENT.EXPORT_PROJECT_REGISTER_PDF, {
  //           label: GA_LABEL.EXPORT_PROJECT_REGISTER_PDF,
  //           // eslint-disable-next-line no-undef
  //           pathname: window?.location?.href,
  //           project_id: projectId,
  //           project_register_id: registerId,
  //           user_id: currentUser?.id,
  //           user_name: currentUser?.name,
  //           tenant_id: currentUser?.tenantUser?.tenant?.id,
  //           tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
  //         });
  //       }}
  //     >
  //       PDF
  //     </Button>
  //   );
  // };

  const renderDownloadCsvButton = () => {
    const token = getToken();
    let url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/export-csv/report/register/${showListView ? 'logs' : 'entries'}?access_token=Bearer ${token}&tenantId=${tenantId}&registerId=${registerId}`;
    if (filterOption?.length > 0) {
      url += `&fromDate=${moment(filterOption[0])?.format(DEFAULTDATEFORMAT)}&toDate=${moment(filterOption[1])?.format(DEFAULTDATEFORMAT)}`;
    }
    return (
      <Button
        type="ghost"
        className="export-button d-flex b-0 justify-center width-percent-100"
        target="_blank"
        href={(showListView ? !isLogsDataEmpty : !isEntriesDataEmpty) && url}
        onClick={() => {
          Event(GA_EVENT.EXPORT_PROJECT_REGISTER_CSV, {
            label: GA_LABEL.EXPORT_PROJECT_REGISTER_CSV,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            project_register_id: registerId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
        }}
      >
        CSV
      </Button>
    );
  };

  return (
    <div className="register-module">
      <div className="d-flex justify-end align-center mb-10">
        <div
          className={`${isDesktopViewport ? 'align-center justify-end' : 'flex-vertical'} d-flex width-percent-100`}
        >
          <div
            className={`${!isDesktopViewport && 'justify-end mb-10'} d-flex align-center`}
          >
            <Tag
              className={REGISTER_STATUS_CLASSNAME[registerDetailsData?.status]}
            >
              {registerDetailsData?.status}
            </Tag>
            <div className="mr-10">
              <Segmented
                options={[REGISTER_VIEW_TYPE.LOGS, REGISTER_VIEW_TYPE.ENTRIES]}
                value={
                  showListView
                    ? REGISTER_VIEW_TYPE.LOGS
                    : REGISTER_VIEW_TYPE.ENTRIES
                }
                onChange={(e) => setShowListView(e === REGISTER_VIEW_TYPE.LOGS)}
              />
            </div>
            {!isDesktopViewport && (
              <div className="d-flex">
                <Dropdown
                  getPopupContainer={() =>
                    // eslint-disable-next-line no-undef
                    document.querySelector('.details-header')
                  }
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: renderDownloadCsvButton(),
                      },
                      // Commented for future use
                      // {
                      //   key: 'pdf',
                      //   label: renderDownloadPdfButton(),
                      // },
                    ],
                  }}
                  trigger={['click']}
                  placement="bottom"
                >
                  <Button
                    shape="round"
                    type="primary"
                    disabled={
                      showListView ? isLogsDataEmpty : isEntriesDataEmpty
                    }
                    icon={<Export height="26px" />}
                    className="export-button fill-white d-flex b-0"
                  />
                </Dropdown>
                <CanPerform
                  action={ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG}
                  type={ALLOWED_ACTION_TYPE.PROJECT}
                >
                  <Button
                    shape="round"
                    icon={<AddButton />}
                    type="primary"
                    disabled={includes(
                      [
                        REGISTER_STATUS_LABEL.DRAFT,
                        REGISTER_STATUS_LABEL.ARCHIVED,
                      ],
                      registerDetailsData?.status,
                    )}
                    onClick={() => {
                      navigate(
                        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/add-set`,
                        {
                          state: {
                            registerProperty:
                              registerDetailsData?.registerDetails,
                            registerDetailsData,
                            currentPath: pathname,
                          },
                        },
                      );
                    }}
                  />
                </CanPerform>
              </div>
            )}
          </div>
          <CustomRangePicker
            className={!isDesktopViewport ? 'width-percent-100 mr-10' : 'mr-10'}
            setDateSelected={(date) => {
              if (date?.length > 0) {
                setFilterOption([
                  moment(date?.[0])?.format(DEFAULTDATETIMEFORMATFORREPORTS),
                  moment(date?.[1])?.format(DEFAULTDATETIMEFORMATFORREPORTS),
                ]);
              } else {
                setFilterOption('');
              }
            }}
            selectedDates={filterOption}
          />
          {isDesktopViewport && (
            <>
              <Space className="d-flex justify-end">
                <Dropdown
                  getPopupContainer={() =>
                    // eslint-disable-next-line no-undef
                    document.querySelector('.details-header')
                  }
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: renderDownloadCsvButton(),
                      },
                      // Commented for future use
                      // {
                      //   key: 'pdf',
                      //   label: renderDownloadPdfButton(),
                      // },
                    ],
                  }}
                  trigger={['click']}
                  placement="bottom"
                >
                  <Button
                    shape="round"
                    type="primary"
                    disabled={
                      showListView ? isLogsDataEmpty : isEntriesDataEmpty
                    }
                    icon={<Export height="26px" />}
                    className="export-button fill-white d-flex b-0"
                  >
                    {isDesktopViewport && 'Export'}
                  </Button>
                </Dropdown>
              </Space>
              <div className="d-flex align-center">
                <CanPerform
                  action={ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG}
                  type={ALLOWED_ACTION_TYPE.PROJECT}
                >
                  <Button
                    shape="round"
                    icon={<AddButton />}
                    disabled={includes(
                      [
                        REGISTER_STATUS_LABEL.DRAFT,
                        REGISTER_STATUS_LABEL.ARCHIVED,
                      ],
                      registerDetailsData?.status,
                    )}
                    type="primary"
                    onClick={() => {
                      navigate(
                        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/add-set`,
                        {
                          state: {
                            registerProperty:
                              registerDetailsData?.registerDetails,
                            registerDetailsData,
                            currentPath: pathname,
                          },
                        },
                      );
                    }}
                  >
                    {isDesktopViewport && 'Add'}
                  </Button>
                </CanPerform>
              </div>
            </>
          )}
        </div>
      </div>
      <div>
        <div className={!showListView && 'd-none'}>
          <SetsTable
            registerDetailsData={registerDetailsData}
            showListView={showListView}
            registerId={registerId}
            setIsDataEmpty={setIsLogsDataEmpty}
            toDate={filterOption[1]}
            fromDate={filterOption[0]}
            registerNavFilter={registerNavFilter}
          />
        </div>
        <div className={showListView && 'd-none'}>
          <EntriesTable
            registerDetailsData={registerDetailsData}
            registerId={registerId}
            showListView={showListView}
            setIsDataEmpty={setIsEntriesDataEmpty}
            toDate={filterOption[1]}
            fromDate={filterOption[0]}
          />
        </div>
      </div>
    </div>
  );
};
export default RegisterDetails;
